if (!customElements.get("custom-bundle")) {
  customElements.define(
    "custom-bundle",
    class CustomBundle extends HTMLElement {

      constructor() {
        super();

        // ── Core refs ─────────────────────────────────
        this._cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this._button = this.querySelector(".custom-bundle-atc");
        this._mainProductId = this.dataset.pid;

        // Cached lists
        this._variantItems  = this.querySelectorAll(`.main-variant-wrapper-item`);
        this._giftItems     = this.querySelectorAll(`.variant-gfit-wrapper-item`);

        // Cached static groups (IMPORTANT)
        this._subPriceEls   = this.querySelectorAll(".price.subscribe");
        this._onePriceEls   = this.querySelectorAll(".price.onetime");
        this._subBadges     = this.querySelectorAll(".custom-variant-saving-badge.subscribe");
        this._oneBadges     = this.querySelectorAll(".custom-variant-saving-badge.onetime");

        // Single elements
        this._onetimeNote    = this.querySelector(".onetime-wrapper-note");
        this._subscribeNote  = this.querySelector(".subscribe-note-wrapper");
        this._subscribeNoteFirstLi = this._subscribeNote?.querySelector("ul li:first-child");
        this._atcPriceWrapper = this._button?.querySelector(".atc-price-wrapper");

        // Cache ATC price nodes
        this._atcCrossed = this._atcPriceWrapper?.querySelector(".atc-o-pirce");
        this._atcSale    = this._atcPriceWrapper?.querySelector(".atc-s-pirce");

        // ── STATE CACHE ───────────────────────────────
        this._stateCache = { plan: null, vid: null };
        this._lastActiveVariant = null;

        // ── PRICE DATA ───────────────────────────────
        this._priceData = {};
        this.querySelectorAll(".custom-price-object[data-vid]").forEach(el => {
          const vid = el.dataset.vid;
          if (!this._priceData[vid]) this._priceData[vid] = {};

          const subWrap = el.querySelector(".subprice-wrapper");
          const oneWrap = el.querySelector(".oneprice-wrapper");

          if (subWrap) {
            this._priceData[vid].sub = {
              crossed: subWrap.querySelector(".atc-o-pirce")?.textContent?.trim() ?? null,
              sale: subWrap.querySelector(".atc-s-pirce")?.textContent?.trim() ?? null,
            };
          }

          if (oneWrap) {
            this._priceData[vid].one = {
              crossed: oneWrap.querySelector(".atc-o-pirce")?.textContent?.trim() ?? null,
              sale: oneWrap.querySelector(".atc-s-pirce")?.textContent?.trim() ?? null,
            };
          }
        });

        this._planPriceData = {};
        this.querySelectorAll(".custom-price-object-for-plan-selector[data-vid]").forEach(el => {
          const vid = el.dataset.vid;
          if (!this._planPriceData[vid]) this._planPriceData[vid] = {};
          this._planPriceData[vid].sub = el.querySelector(".s-price-object")?.innerHTML ?? null;
          this._planPriceData[vid].one = el.querySelector(".o-price-object")?.innerHTML ?? null;
        });

        this._subscribeLabelPrice = this.querySelector(".subscribe-item .plan-price");
        this._onetimeLabelPrice   = this.querySelector(".onetime-item .plan-price");

        this._pendingRaf = null;

        this._initEventListeners();
      }

      // ── EVENTS ────────────────────────────────────
      _initEventListeners() {
        this.addEventListener("change", (e) => {
          if (
            e.target.name === "plan-selector" ||
            e.target.classList.contains("custom-variant-selection-bundle")
          ) {
            this._scheduleUpdate();
          }
        });

        if (this._button) {
          this._button.addEventListener("click", this._addBundle.bind(this));
        }
      }

      // ── RAF BATCH ─────────────────────────────────
      _scheduleUpdate() {
        if (this._pendingRaf) return;
        this._pendingRaf = requestAnimationFrame(() => {
          this._pendingRaf = null;
          this._applyUpdate();
        });
      }

      // ── READ STATE (DIFF BASED) ───────────────────
      _readState() {
        const planEl = this.querySelector(`input[name="plan-selector"]:checked`);
        const variantEl = this.querySelector(`.custom-variant-selection-bundle:checked`);

        const newPlan = planEl?.value ?? 'subscribe';
        const newVid  = variantEl?.value ?? null;

        if (
          this._stateCache.plan === newPlan &&
          this._stateCache.vid === newVid
        ) return null;

        this._stateCache.plan = newPlan;
        this._stateCache.vid  = newVid;

        return {
          plan: newPlan,
          variantEl,
          vid: newVid,
          freeNum: +(variantEl?.dataset.freenumber ?? 0),
          fixText: variantEl?.dataset.fixtext ?? '',
        };
      }

      // ── APPLY UPDATE ─────────────────────────────
      _applyUpdate() {
        const state = this._readState();
        if (!state || !state.variantEl) return;

        const isSubscribe = state.plan === 'subscribe';
        const prices = this._priceData[state.vid] ?? {};
        const planPrices = this._planPriceData[state.vid] ?? {};

        // 🔥 1. Toggle root class (BIG WIN)
        this.classList.toggle("is-subscribe", isSubscribe);
        this.classList.toggle("is-onetime", !isSubscribe);

        // 🔥 2. Variant active (NO LOOP)
        if (this._lastActiveVariant) {
          this._lastActiveVariant.classList.remove("active-variant-selected");
        }
        const parent = state.variantEl.closest(".main-variant-wrapper-item");
        if (parent) {
          parent.classList.add("active-variant-selected");
          this._lastActiveVariant = parent;
        }

        // 🔥 3. Gifts (DIFF ONLY)
        this._giftItems.forEach(el => {
          const num = +(el.dataset.number ?? 0);
          const active = isSubscribe && num <= state.freeNum;

          if (el._active !== active) {
            el.classList.toggle("not-active", !active);
            el._active = active;
          }
        });

        // 🔥 4. Notes
        this._onetimeNote?.classList.toggle("hidden", isSubscribe);
        this._subscribeNote?.classList.toggle("hidden", !isSubscribe);

        if (isSubscribe && this._subscribeNoteFirstLi) {
          if (this._subscribeNoteFirstLi.textContent !== state.fixText) {
            this._subscribeNoteFirstLi.textContent = state.fixText;
          }
        }

        // 🔥 5. ATC price (NO query)
        if (this._atcPriceWrapper) {
          const priceSet = isSubscribe ? prices.sub : prices.one;
          if (priceSet) {
            if (this._atcCrossed) {
              this._atcCrossed.textContent = priceSet.crossed ?? "";
              this._atcCrossed.style.display = priceSet.crossed ? "" : "none";
            }
            if (this._atcSale && this._atcSale.textContent !== priceSet.sale) {
              this._atcSale.textContent = priceSet.sale ?? "";
            }
          }
        }

        // 🔥 6. Plan label prices
        if (isSubscribe && planPrices.sub && this._subscribeLabelPrice) {
          this._subscribeLabelPrice.innerHTML = planPrices.sub;
        }
        if (!isSubscribe && planPrices.one && this._onetimeLabelPrice) {
          this._onetimeLabelPrice.innerHTML = planPrices.one;
        }
      }
      _getState() {
        const planEl = this.querySelector(`input[name="plan-selector"]:checked`);
        const variantEl = this.querySelector(`.custom-variant-selection-bundle:checked`);

        return {
          plan: planEl?.value ?? 'subscribe',
          variantEl,
          vid: variantEl?.value ?? null,
          freeNum: +(variantEl?.dataset.freenumber ?? 0),
          fixText: variantEl?.dataset.fixtext ?? '',
        };
      }
      // ── ADD TO CART (DEFERRED) ───────────────────
      _addBundle(evt) {
        evt.preventDefault();

         const state = this._getState(); // ✅ ALWAYS fresh

        if (!state.variantEl) return;

        const items = [];
        const bundleId = Date.now();
        const planId = state.variantEl.dataset.sellingplan;

        if (state.plan === 'subscribe') {
          this._giftItems.forEach(el => {
            if (+(el.dataset.number ?? 0) <= state.freeNum) {
              items.push({
                id: el.dataset.gid,
                quantity: 1,
                properties: { _ismain: 0, _bundleid: bundleId }
              });
            }
          });

          items.push({
            id: state.variantEl.value,
            quantity: 1,
            selling_plan: planId,
            properties: { _ismain: 1, _bundleid: bundleId }
          });
        } else {
          items.push({ id: state.variantEl.value, quantity: 1 });
        }
        console.log(state.plan,items,"custom logggggggg");
        if (!items.length || !this._cart) return;

        this._button.disabled = true;

        setTimeout(() => {
          fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items,
              sections: this._cart.getSectionsToRender().map(s => s.id),
              sections_url: window.location.pathname
            })
          })
            .then(r => r.json())
            .then(response => {

              this._cart.renderContents(response);

            })
            .catch(console.error)
            .finally(() => {
              this._button.disabled = false;
               if (this._cart && this._cart.classList.contains('is-empty')) this._cart.classList.remove('is-empty');
            });
        }, 0);
      }

      connectedCallback() {
        requestAnimationFrame(() => this._applyUpdate());
      }
    }
  );
}